import React, { useEffect, useState } from 'react'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { useAuthContext } from '../../hooks/useAuthContext'
import { Line } from 'react-chartjs-2'
import {
    getDeviceReadingsPastHour,
    getDeviceReadingsPastDay,
    getDeviceReadingsPastWeek,
    getDeviceReadingsPastMonth,
} from '../../helper/deviceHelper'
import { DeviceListItem } from '../../types'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

type ReadingRow = { hour: string; minute?: string; avgValue?: number }

type ReadingData = {
    labels: string[]
    datasets: {
        label: string
        backgroundColor: string
        borderColor: string
        color: string
        data: number[]
    }[]
}

function emptyChartData(): ReadingData {
    return {
        labels: [],
        datasets: [
            {
                label: '',
                backgroundColor: '',
                borderColor: '',
                color: '',
                data: [],
            },
        ],
    }
}

type LoadState = 'idle' | 'loading' | 'ready' | 'empty' | 'error'

function ChartStateMessage({
    minHeightClass,
    children,
    role,
}: {
    minHeightClass: string
    children: React.ReactNode
    role?: 'status'
}) {
    return (
        <div
            className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-lg border border-zinc-800/80 bg-zinc-950/40 px-4 py-10 text-center',
                minHeightClass
            )}
            role={role}
            aria-live={role === 'status' ? 'polite' : undefined}
        >
            {children}
        </div>
    )
}

interface LineChartProps {
    device_list: DeviceListItem[]
    timeInterval: string
    device_type: string
}

const LineChart = ({ device_list, timeInterval, device_type }: LineChartProps) => {
    const [data, setData] = useState<ReadingData>(emptyChartData)
    const [loadState, setLoadState] = useState<LoadState>('loading')
    const { user } = useAuthContext()

    function getRandomColor() {
        const letters = '0123456789ABCDEF'
        let color = '#'
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)]
        }
        return color
    }

    function convertTo12Hour(time: string) {
        const date = new Date(time)
        const month = date.getMonth() + 1
        const day = date.getDate()
        const year = date.getFullYear()
        let hours = date.getHours()
        const minutes = date.getMinutes()
        const ampm = hours >= 12 ? 'pm' : 'am'
        hours = hours % 12
        hours = hours ? hours : 12
        const minStr = Number(minutes < 10 ? '0' + minutes : minutes)
        return month + '/' + day + '/' + year + ' ' + hours + ':' + minStr + ' ' + ampm
    }

    const getReadingUnit = (): string => {
        const device_units = [
            { device_type: 'Electric', unit: 'mS/cm' },
            { device_type: 'pH', unit: 'pH' },
            { device_type: 'Temperature', unit: '°F' },
            { device_type: 'Humidity', unit: '%' },
            { device_type: 'TDS', unit: 'ppm' },
            { device_type: 'Water Flow Sensor', unit: 'L/min' },
            { device_type: 'Moisture', unit: '%' },
        ]
        for (const device of device_units) {
            if (device_type.includes(device.device_type)) {
                return device.unit
            }
        }
        return ''
    }

    const getSubtitle = () => {
        const timeRange = timeInterval.charAt(0).toUpperCase() + timeInterval.slice(1)
        const deviceTypeLabels: Record<string, string> = {
            Electric: 'EC readings over the past ',
            pH: 'pH readings over the past ',
            Temperature: 'Temperature readings over the past ',
            Humidity: 'Humidity readings over the past ',
            Moisture: 'Moisture readings over the past ',
            'Water Flow Sensor': 'Water flow readings over the past ',
            TDS: 'TDS readings over the past ',
        }

        let label = ''
        for (const deviceType of Object.keys(deviceTypeLabels)) {
            if (device_type.includes(deviceType)) {
                label = deviceTypeLabels[deviceType] + timeRange
            }
        }
        return label
    }

    useEffect(() => {
        let cancelled = false

        const run = async () => {
            if (!device_list?.length) {
                return
            }
            if (!user) {
                setLoadState('idle')
                setData(emptyChartData())
                return
            }

            setLoadState('loading')

            const timeIntervalFunctions = [
                { time: 'hour', getReadings: getDeviceReadingsPastHour },
                { time: 'day', getReadings: getDeviceReadingsPastDay },
                { time: 'week', getReadings: getDeviceReadingsPastWeek },
                { time: 'month', getReadings: getDeviceReadingsPastMonth },
            ] as const

            const getter = timeIntervalFunctions.find((t) => t.time === timeInterval)?.getReadings
            if (!getter) {
                if (!cancelled) setLoadState('error')
                return
            }

            try {
                const results = await Promise.all(
                    device_list.map(async (device) => {
                        const readings = await getter(user, device.device_id)
                        const randomColor = getRandomColor()
                        return {
                            label: device.name,
                            backgroundColor: 'black',
                            borderColor: randomColor,
                            color: 'green',
                            data: Array.isArray(readings)
                                ? readings.map((reading: { avgValue: number }) => reading.avgValue)
                                : [],
                            readings: Array.isArray(readings) ? (readings as ReadingRow[]) : [],
                        }
                    })
                )

                if (cancelled) return

                const newDatasets = results.map(({ readings: _r, ...rest }) => rest)
                const labelSource = results.find((r) => r.readings.length > 0)?.readings ?? []
                const sorted = [...labelSource].sort(
                    (a, b) =>
                        Number(new Date(a.hour + (a.minute != undefined ? ' ' + a.minute : ''))) -
                        Number(new Date(b.hour + (b.minute != undefined ? ' ' + b.minute : '')))
                )
                const labels = sorted.map((reading) =>
                    convertTo12Hour(reading.hour + (reading.minute != undefined ? ' ' + reading.minute : ''))
                )

                const hasPoints = results.some((r) => r.data.some((v) => v != null && !Number.isNaN(v)))
                const next: ReadingData = { labels, datasets: newDatasets }

                if (!labels.length && !hasPoints) {
                    setData(next)
                    setLoadState('empty')
                    return
                }

                setData(next)
                setLoadState('ready')
            } catch {
                if (!cancelled) {
                    setLoadState('error')
                    setData(emptyChartData())
                }
            }
        }

        void run()
        return () => {
            cancelled = true
        }
    }, [timeInterval, device_list, user])

    const style = {
        background: 'white',
        border: 'none',
        borderRadius: '8px',
        boxShadow: '0 2px 10px 0 rgba(70, 76, 79, .2)',
        height: '420px',
        width: '100%',
    }

    const plugins = {
        title: {
            display: true,
            text: [device_type, getSubtitle()],
        },
        tooltip: {
            titleColor: 'green',
        },
    }

    const scales: Record<string, unknown> = {
        y: {
            display: true,
            title: {
                display: true,
                text: getReadingUnit(),
            },
        },
    }

    if (!device_list?.length) {
        return null
    }

    return (
        <div
            className={cn(
                'mx-auto mb-4 w-full max-w-3xl rounded-xl border border-zinc-700/90 bg-zinc-900/50 p-3 shadow-lg sm:p-4'
            )}
        >
            {loadState === 'loading' || loadState === 'idle' ? (
                <ChartStateMessage
                    minHeightClass="min-h-[280px] sm:min-h-[320px]"
                    role={loadState === 'loading' ? 'status' : undefined}
                >
                    {loadState === 'loading' ? (
                        <Loader2 className="size-9 animate-spin text-emerald-400" aria-hidden />
                    ) : null}
                    <p className="text-sm font-medium text-zinc-200">
                        {loadState === 'idle' ? 'Sign in required to load charts.' : 'Loading readings…'}
                    </p>
                    <p className="max-w-sm text-xs text-zinc-500">{getSubtitle()}</p>
                </ChartStateMessage>
            ) : null}

            {loadState === 'error' ? (
                <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-lg border border-red-900/40 bg-red-950/30 px-4 py-8 text-center">
                    <p className="text-sm font-medium text-red-200">Couldn&apos;t load this chart</p>
                    <p className="max-w-sm text-xs text-red-200/80">
                        Check your connection and try refreshing. If it keeps happening, the service may be busy.
                    </p>
                </div>
            ) : null}

            {loadState === 'empty' ? (
                <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-lg border border-zinc-800/80 bg-zinc-950/40 px-4 py-8 text-center">
                    <p className="text-sm font-medium text-zinc-200">No readings in this range</p>
                    <p className="max-w-sm text-xs text-zinc-500">
                        There&apos;s no data for these devices over the selected time period. Try a longer range or
                        confirm sensors are reporting.
                    </p>
                </div>
            ) : null}

            {loadState === 'ready' ? (
                <div className="min-h-[280px] w-full sm:min-h-[320px]">
                    <Line
                        style={style}
                        data={data}
                        options={{
                            maintainAspectRatio: false,
                            responsive: true,
                            plugins: plugins,
                            scales: scales,
                        }}
                    />
                </div>
            ) : null}

        </div>
    )
}

export default LineChart
